import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, getUser } from "../api";
import { navUrl } from "../lib/navUrl";
import { toast } from "sonner";

const eur = (c) => (typeof c === "number" && c > 0 ? `€ ${(c / 100).toFixed(2)}` : "—");

// Prenotazione servizio (paziente). Riusa il pattern della visita in presenza.
export const ServiceBooking = ({ service, orgId, onClose }) => {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [slots, setSlots] = useState(null);
  const [selDay, setSelDay] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/services/${service.id}/slots`).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, [service.id]);

  const days = useMemo(() => {
    if (!slots) return [];
    const m = new Map(); const now = Date.now();
    for (const s of slots) {
      if (new Date(s.start_at).getTime() <= now) continue;
      const k = s.local_date; if (!m.has(k)) m.set(k, []); m.get(k).push(s);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [slots]);
  useEffect(() => { if (days.length && !days.some(([k]) => k === selDay)) setSelDay(days[0][0]); }, [days, selDay]);

  const isFree = !service.price_cents;
  const pay = async () => {
    if (!u) {
      localStorage.setItem("pending_action", JSON.stringify({ type: "book_service", kind: "org", id: orgId, service_id: service.id }));
      window.location.href = `/login?next=${encodeURIComponent(`/organizations/${orgId}`)}`; return;
    }
    setBusy(true);
    try {
      if (isFree) {
        await api.post(`/services/${service.id}/book-free`, { slot_start: sel.start_at });
        toast.success(t("services.free_booked")); onClose(); return;
      }
      const { data } = await api.post(`/services/${service.id}/create-checkout`, { slot_start: sel.start_at, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); setBusy(false); }
  };

  const proName = service.professional?.name;
  const selSlots = days.find(([k]) => k === selDay)?.[1] || [];
  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="service-booking-overlay">
      <div className="yp-card p-6 max-w-lg w-full bg-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="service-booking">
        <h3 className="text-lg font-bold" data-testid="service-booking-name">{service.name}</h3>
        <div className="text-sm text-slate-500 mt-0.5">{eur(service.price_cents)} · {service.duration_minutes} min{proName ? ` · ${proName}` : ""}</div>
        {slots === null ? <div className="text-slate-400 mt-3">{t("common.loading")}</div>
          : days.length === 0 ? <p className="text-slate-500 mt-3" data-testid="service-no-slots">{t("services.no_slots")}</p>
            : (<>
              <div className="flex gap-2 overflow-x-auto pb-2 mt-3" role="tablist" data-testid="service-days">
                {days.map(([k]) => (
                  <button key={k} role="tab" aria-selected={selDay === k} onClick={() => setSelDay(k)} data-testid="service-day"
                    className={`yp-btn !py-1.5 !px-3 text-sm shrink-0 ${selDay === k ? "yp-btn-primary" : "yp-btn-outline"}`}>
                    {new Date(`${k}T12:00:00`).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short" })}
                  </button>))}
              </div>
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="service-times">
                {selSlots.map((s, i) => (
                  <button key={i} onClick={() => setSel(s)} data-testid="service-slot-btn"
                    className={`yp-card p-2 text-sm text-center hover:border-blue-400 transition-colors ${sel?.start_at === s.start_at ? "border-blue-500" : ""}`}>
                    {s.local_time}
                  </button>))}
              </div>
            </>)}

        {sel && (
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="service-summary">
            <h4 className="font-semibold mb-2">{t("services.summary")}</h4>
            <dl className="text-sm space-y-1">
              <div className="flex justify-between"><dt className="text-slate-500">{t("services.service")}</dt><dd className="font-medium">{service.name}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{t("services.facility")}</dt><dd>{service.org_name || ""}</dd></div>
              {proName && <div className="flex justify-between"><dt className="text-slate-500">{t("services.professional_optional")}</dt><dd>{proName}</dd></div>}
              <div className="flex justify-between"><dt className="text-slate-500">{t("services.datetime")}</dt><dd data-testid="service-sum-when">{new Date(sel.start_at).toLocaleString(i18n.language)}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">{t("services.duration")}</dt><dd>{sel.duration_minutes} min</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">📍</dt><dd className="text-right" data-testid="service-sum-place">{sel.location_label}</dd></div>
              <div className="flex justify-between border-t border-slate-100 pt-2 mt-2"><dt className="font-semibold">{t("services.total")}</dt><dd className="font-semibold" data-testid="service-sum-total">{eur(sel.price_cents)}</dd></div>
            </dl>
            {navUrl(sel.nav_address || sel.location_label) && (
              <a href={navUrl(sel.nav_address || sel.location_label)} target="_blank" rel="noopener noreferrer"
                className="yp-btn yp-btn-outline w-full text-sm mt-3 inline-block text-center" data-testid="service-open-maps">📍 {t("services.open_maps")}</a>)}
          </div>)}
        <div className="flex gap-2 mt-4">
          <button className="yp-btn yp-btn-ghost !px-4" onClick={onClose} data-testid="service-close">{t("services.cancel")}</button>
          <button className="yp-btn yp-btn-primary flex-1" onClick={pay} disabled={!sel || busy} data-testid="service-pay-btn">{isFree ? t("services.book_free") : t("services.book")}</button>
        </div>
      </div>
    </div>);
};
